import { useCallback, useState } from 'react';
import type { InviteShare } from './inviteShare';

/**
 * Owns the redeem links waiting to be shown. Every surface that can mint a
 * link (the send form, a per-row resend) funnels into one dialog, so the
 * links a user has not copied yet cannot be knocked off screen by the next
 * action.
 */
export function useInviteShares() {
  const [shares, setShares] = useState<InviteShare[] | null>(null);

  /**
   * Adds links to the open dialog, replacing any earlier entry for the same
   * invite id: a re-issue invalidates the token the previous row shows, so
   * keeping both would offer a dead link beside the live one.
   */
  const addShares = useCallback((incoming: InviteShare[]) => {
    if (incoming.length === 0) return;
    const replaced = new Set(incoming.map((share) => share.invite.id));
    setShares((previous) => [
      ...(previous ?? []).filter((share) => !replaced.has(share.invite.id)),
      ...incoming,
    ]);
  }, []);

  const clearShares = useCallback(() => setShares(null), []);

  return { shares, addShares, clearShares };
}
